import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  SportEvent,
  Team,
  Court,
  Game,
  League,
  LeagueEvent,
  Player,
  GameStats,
  Schedule,
  CreateSportEvent,
  DetailedTeam,
  LoginResponse,
} from '../types';
import { RootState } from './store';
import {
  CreatePostResponse,
  GameView,
  GetFeedResponse,
  PlayerStats,
  PostView,
  Round,
  SportEventConfig,
  SportEventView,
  UpdatePlayerStats,
} from '../types/dto';

const apiUrl = `${
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://api.yogicup.app/prod'
    : 'http://localhost:8080')
}/api/v2/`;

// Define a service using a base URL and expected endpoints
export const yogiCupApi = createApi({
  reducerPath: 'yogicupApi',
  keepUnusedDataFor: 300,
  baseQuery: fetchBaseQuery({
    baseUrl: apiUrl,
    prepareHeaders: (headers: Headers, { getState }) => {
      const token = (getState() as RootState).user.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
    credentials: 'include',
  }),
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, { email: string; password: string }>(
      {
        query: ({ email, password }) => ({
          url: '/user/login',
          method: 'POST',
          body: { email, password },
        }),
      }
    ),
    validate: builder.mutation<boolean, string>({
      query: (token) => ({
        url: '/user/validate',
        method: 'POST',
        body: token,
      }),
    }),
    refresh: builder.mutation<LoginResponse, void>({
      query: () => ({
        url: '/user/refresh',
        method: 'GET',
      }),
    }),
    getAllEvents: builder.query<SportEventView[], void>({
      query: () => `event`,
    }),
    getEventById: builder.query<SportEventView, number>({
      query: (id) => `event/${id}`,
    }),
    getTeamById: builder.query<DetailedTeam, number>({
      query: (id) => `team/${id}`,
    }),
    getGameById: builder.query<GameView, number>({
      query: (id) => `game/${id}`,
    }),
    getPlayerById: builder.query<Player, number>({
      query: (id) => `player/${id}`,
    }),
    getLeagueById: builder.query<League, number>({
      query: (id) => `league/${id}`,
    }),
    getCourtById: builder.query<Court, number>({
      query: (id) => `court/${id}`,
    }),
    getAllTeams: builder.query<Team[], void>({
      query: () => `team`,
    }),
    getAllCourts: builder.query<Court[], void>({
      query: () => `court`,
    }),
    getAllGames: builder.query<Game[], void>({
      query: () => `game`,
    }),
    generateSchedule: builder.mutation<
      Schedule,
      {
        eventId: number;
        timeslots: [Date, Date][];
        gameDuration: number;
        timeBetweenGames: number;
      }
    >({
      query: ({ eventId, ...rest }) => ({
        url: `event/${eventId}/plan`,
        method: 'POST',
        body: rest,
      }),
    }),
    importSchedule: builder.mutation<
      Schedule,
      { schedule: Schedule; eventId: number }
    >({
      query: ({ schedule, eventId }) => ({
        url: `event/${eventId}/schedule`,
        method: 'POST',
        body: schedule,
      }),
    }),
    saveSchedule: builder.mutation<
      Schedule,
      { schedule: Schedule; eventId: number }
    >({
      query: ({ schedule, eventId }) => ({
        url: `event/${eventId}/schedule/edit`,
        method: 'POST',
        body: schedule,
      }),
    }),
    getAllLeagues: builder.query<League[], void>({
      query: () => `league`,
    }),
    getAllPlayers: builder.query<Player[], void>({
      query: () => `player`,
    }),
    getScheduleForEvent: builder.query<Schedule, number>({
      query: (eventId) => ({
        url: `event/${eventId}/schedule`,
        params: { tz: new Date().getTimezoneOffset() / 60 },
      }),
    }),
    getTeamsForEvent: builder.query<Team[], number>({
      query: (eventId) => `event/${eventId}/teams`,
    }),
    getPlayersForEvent: builder.query<Player[], number>({
      query: (eventId) => `event/${eventId}/players`,
    }),
    getLeaguesForEvent: builder.query<LeagueEvent[], number>({
      query: (eventId) => `event/${eventId}/leagues`,
    }),
    getCourtsForEvent: builder.query<Court[], number>({
      query: (eventId) => `event/${eventId}/courts`,
    }),
    getGameStats: builder.query<GameStats, number>({
      query: (id) => `game/${id}/stats`,
    }),
    updateTeam: builder.mutation<Team, { id: number; team: Partial<Team> }>({
      query: ({ team, id }) => ({
        url: `team/${id}`,
        method: 'PUT',
        body: team,
      }),
    }),
    updateEvent: builder.mutation<
      SportEventView,
      {
        id: number;
        event: Partial<SportEvent>;
        config: Partial<SportEventConfig>;
      }
    >({
      query: ({ id, event, config }) => ({
        url: `event/${id}`,
        method: 'PUT',
        body: { event, config },
      }),
    }),
    checkInPlayer: builder.mutation<boolean, { id: number }>({
      query: ({ id }) => ({
        url: `player/${id}/checkin`,
        method: 'PUT',
      }),
    }),
    startGame: builder.mutation<Game, { id: number }>({
      query: ({ id }) => ({
        url: `game/${id}/start`,
        method: 'POST',
      }),
    }),
    finishGame: builder.mutation<Game, { id: number }>({
      query: ({ id }) => ({
        url: `game/${id}/finish`,
        method: 'POST',
      }),
    }),
    verifyGame: builder.mutation<Game, { id: number }>({
      query: ({ id }) => ({
        url: `game/${id}/verify`,
        method: 'POST',
      }),
    }),
    uploadEventData: builder.mutation<
      boolean,
      { eventId: number; file: FormData }
    >({
      query: ({ file, eventId }) => ({
        url: `/event/${eventId}/upload`,
        method: 'POST',
        body: file,
      }),
    }),
    processEventData: builder.mutation<boolean, number>({
      query: (eventId) => ({
        url: `/event/${eventId}/process`,
        method: 'GET',
      }),
    }),
    upsertCourts: builder.mutation<
      Court[],
      { courts: Partial<Court>[]; eventId: number }
    >({
      query: ({ courts, eventId }) => ({
        url: `event/${eventId}/courts`,
        method: 'POST',
        body: courts,
      }),
    }),
    updatePlayerStats: builder.mutation<
      PlayerStats,
      { id: number; stats: UpdatePlayerStats }
    >({
      query: ({ stats, id }) => ({
        url: `scoring/${id}`,
        method: 'PUT',
        body: stats,
      }),
    }),
    updateLeagueEvent: builder.mutation<
      LeagueEvent,
      { eventId: number; leagueId: number; leagueEvent: Partial<LeagueEvent> }
    >({
      query: ({ leagueEvent, leagueId, eventId }) => ({
        url: `event/${eventId}/league/${leagueId}`,
        method: 'PUT',
        body: leagueEvent,
      }),
    }),
    createEvent: builder.mutation<SportEvent, CreateSportEvent>({
      query: (event) => ({
        url: `event`,
        method: 'POST',
        body: event,
      }),
    }),
    updateGame: builder.mutation<Game, { id: number; game: Partial<Game> }>({
      query: ({ game, id }) => ({
        url: `game/${id}`,
        method: 'PUT',
        body: game,
      }),
    }),
    rankTeams: builder.mutation<Team[], { eventId: number; leagueId: number }>({
      query: ({ eventId, leagueId }) => ({
        url: `event/${eventId}/teams/${leagueId}/ranking`,
        method: 'GET',
      }),
    }),
    getRankings: builder.query<
      Team[],
      { eventId: number; leagueId: number; refresh: boolean }
    >({
      query: ({ eventId, leagueId, refresh }) => ({
        url: `event/${eventId}/teams/${leagueId}/ranking`,
        params: { refresh },
      }),
    }),
    generatePlayoffSchedule: builder.mutation<
      Round[],
      {
        eventId: number;
        leagueId: number;
        numSeeds?: number;
        persist?: boolean;
        date: Date;
      }
    >({
      query: ({ eventId, leagueId, numSeeds, persist, date }) => ({
        url: `event/${eventId}/playoffs/${leagueId}/schedule/new`,
        method: 'GET',
        params: { numSeeds, persist, date },
      }),
    }),
    getPlayoffSchedule: builder.mutation<
      Round[],
      { eventId: number; leagueId: number }
    >({
      query: ({ eventId, leagueId }) =>
        `event/${eventId}/playoffs/${leagueId}/schedule`,
    }),
    getFeed: builder.query<
      GetFeedResponse,
      { eventId: number; cursor?: number; approved?: boolean }
    >({
      query: ({ eventId, cursor, approved }) => {
        return {
          url: `feed/${eventId}`,
          params: { cursor, approved },
        };
      },
    }),
    createPost: builder.mutation<
      CreatePostResponse,
      {
        eventId: number;
        title: string;
        description: string;
        fileType: string;
        fileName: string;
        timestamp: string;
      }
    >({
      query: ({
        eventId,
        fileType,
        title,
        description,
        timestamp,
        fileName,
      }) => {
        return {
          url: `feed/${eventId}/media`,
          method: 'POST',
          body: { title, description, timestamp, fileType, fileName },
        };
      },
    }),
    makePostDecision: builder.mutation<
      boolean,
      { eventId: number; postId: string; approved: boolean }
    >({
      query: ({ eventId, postId, approved }) => {
        return {
          url: `feed/${eventId}/post/${postId}/${
            approved ? 'approve' : 'reject'
          }`,
          method: 'POST',
        };
      },
    }),
    deletePost: builder.mutation<boolean, { eventId: number; postId: string }>({
      query: ({ eventId, postId }) => {
        return {
          url: `feed/${eventId}/post/${postId}`,
          method: 'DELETE',
        };
      },
    }),
  }),
});

export const {
  useGetAllEventsQuery,
  useGetEventByIdQuery,
  useGetAllTeamsQuery,
  useGetAllCourtsQuery,
  useGetAllGamesQuery,
  useGetAllLeaguesQuery,
  useGetAllPlayersQuery,
  useGetScheduleForEventQuery,
  useGetGameStatsQuery,
  useCreateEventMutation,
  useGetTeamsForEventQuery,
  useGetPlayersForEventQuery,
  useGetLeaguesForEventQuery,
  useGetCourtsForEventQuery,
  useGetTeamByIdQuery,
  useGetGameByIdQuery,
  useGetPlayerByIdQuery,
  useGetLeagueByIdQuery,
  useGetCourtByIdQuery,
  useUpdatePlayerStatsMutation,
  useUpdateTeamMutation,
  useLoginMutation,
  useValidateMutation,
  useRefreshMutation,
  useUpsertCourtsMutation,
  useStartGameMutation,
  useVerifyGameMutation,
  useUploadEventDataMutation,
  useProcessEventDataMutation,
  useUpdateLeagueEventMutation,
  useGenerateScheduleMutation,
  useSaveScheduleMutation,
  useImportScheduleMutation,
  useCheckInPlayerMutation,
  useFinishGameMutation,
  useGeneratePlayoffScheduleMutation,
  useUpdateGameMutation,
  useGetRankingsQuery,
  useGetPlayoffScheduleMutation,
  useUpdateEventMutation,
  useGetFeedQuery,
  useCreatePostMutation,
  useMakePostDecisionMutation,
  useDeletePostMutation,
} = yogiCupApi;

export const {
  endpoints: { login, validate, refresh },
} = yogiCupApi;
